import {useQueries, useQuery, useQueryClient} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import type {Geometry} from 'geojson';
import {useEffect} from 'react';
import urls from './urls.js';
import {DURATION_MS, getQueryKeys} from './util.js';
import {request} from './index.js';
import {Collection, Layer} from '@src/types/configurations.ts';

export type SearchQuery = {
  bbox?: number[];
  datetime: string;
  collections: string[];
  limit?: number;
  distinct?: string;
};

type FeatureAsset = {
  href: string;
  title?: string;
  description?: string;
  type?: string;
  roles?: string[];
};

type FeatureLink = {
  href: string;
  rel: string;
  type?: string;
  title?: string;
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: object;
  merge?: boolean;
};
type SearchResultFeature = {
  id: string;
  type: 'Feature';
  stac_version: string;
  stac_extensions: string[];
  bbox:
    | [number, number, number, number]
    | [number, number, number, number, number]
    | [number, number, number, number, number, number];
  geometry: Geometry;
  links: FeatureLink[];
  assets: Record<string, FeatureAsset>;
  properties: {
    datetime: string | null;
  } & Record<string, unknown>;
};

type SearchResultContext = {
  limit?: number;
  next?: number;
  returned: number;
};

export type ServiceLocation = {
  id: string;
  url: string;
  wmsUrl: string;
  processUrl: string;
  catalogUrl: string;
};

export type CatalogSearchResults<FeatureDistinctType> = {
  type: 'FeatureCollection';
  context: SearchResultContext;
  links: FeatureLink[];
  features: FeatureDistinctType[];
};

export const getCatalogSearch = <
  T extends string | SearchResultFeature = SearchResultFeature,
>(
  query: SearchQuery,
  catalogUrl: string,
): Promise<CatalogSearchResults<T>> => {
  return request
    .post(`${catalogUrl}/search`, query, {
      headers: {Accept: 'application/geo+json'},
    })
    .then((res: AxiosResponse) => res.data);
};

const getCollection = (collectionId: string) => {
  return request
    .get<Layer>(`${urls.catalog}/collections/${collectionId}`)
    .then((res: AxiosResponse<Layer>) => res.data);
};

const getServiceLocations = () => {
  return request
    .get<ServiceLocation[]>(urls.shServiceLocations)
    .then((res: AxiosResponse<ServiceLocation[]>) => res.data);
};

const getCollectionsForServiceLocation = (catalogUrl: string) => {
  return request
    .get<ServiceLocation[]>(`${catalogUrl}/collections`)
    .then((res: AxiosResponse<ServiceLocation[]>) => res.data);
};

const catalogQueryKeys = getQueryKeys('catalog');
const INIT_KEY = ['public-collections-initialized'];

const removeCDSEFromLocations = (locations: ServiceLocation[]) => {
  return locations.filter(location => location.id !== 'cdse');
};

export const useGetServiceLocations = (options?: {enabled: boolean}) => {
  return useQuery<ServiceLocation[]>({
    queryKey: catalogQueryKeys.list({query: 'locations'}),
    queryFn: getServiceLocations,
    staleTime: DURATION_MS.INFINITY,
    select: removeCDSEFromLocations,
    enabled: options?.enabled ?? true,
  });
};

export const useInitCollectionsFromServices = () => {
  const queryClient = useQueryClient();
  const isInitialized = queryClient.getQueryData(INIT_KEY);

  const {data: locations, isSuccess: locationsLoaded} = useGetServiceLocations({
    enabled: !isInitialized,
  });

  const collectionsResponses = useQueries({
    queries: (locations ?? []).map(location => ({
      queryKey: catalogQueryKeys.list({query: `collections-${location.id}`}),
      queryFn: () => getCollectionsForServiceLocation(location.catalogUrl),
      select: (data: {collections: Collection[]}) => data.collections,
      staleTime: DURATION_MS.INFINITY,
      enabled: !!location.catalogUrl && !isInitialized,
    })),
  });

  const allDone =
    locationsLoaded &&
    collectionsResponses.every(r => r.isSuccess || r.isError);

  // Breaking up initial request and storing each collection individually so that future requests for
  // specific collections (via useGetCollection) can use cached data instead of making new API calls
  useEffect(() => {
    if (!allDone) {
      return;
    }
    collectionsResponses.forEach(response => {
      if (response.data) {
        response.data.forEach((collection: Collection) => {
          const key = catalogQueryKeys.detail(collection.id);
          if (!queryClient.getQueryData(key)) {
            queryClient.setQueryData(key, collection);
          }
        });
      }
    });
    queryClient.setQueryData(INIT_KEY, true);
  }, [collectionsResponses, queryClient, allDone]);

  return {isLoading: !isInitialized && !allDone};
};

export const useGetCollection = (collectionId: string) => {
  const {isLoading} = useInitCollectionsFromServices();
  const query = useQuery<Collection>({
    queryKey: catalogQueryKeys.detail(collectionId),
    queryFn: () => getCollection(collectionId),
    enabled: !!collectionId && !isLoading,
    staleTime: DURATION_MS.INFINITY,
  });
  return {
    ...query,
    isLoading: !!collectionId && (isLoading || query.isLoading),
  };
};

export const useGetCollections = (collectionIds: string[]) => {
  const {isLoading} = useInitCollectionsFromServices();
  const queries = useQueries({
    queries: collectionIds.map(collectionId => ({
      queryKey: catalogQueryKeys.detail(collectionId),
      queryFn: () => getCollection(collectionId),
      enabled: !!collectionId && !isLoading,
      staleTime: DURATION_MS.INFINITY,
    })),
  });

  return queries.map((query, index) => ({
    ...query,
    isLoading: !!collectionIds[index] && (isLoading || query.isLoading),
  }));
};

export enum SHURLs {
  process = 'processUrl',
  wms = 'wmsUrl',
  catalog = 'catalogUrl',
  base = 'url',
}

export const useGetServiceLocationForCollection = (
  collectionId: string,
  type: SHURLs = SHURLs.base,
) => {
  const {data: locations, isLoading: locationsLoading} =
    useGetServiceLocations();
  const {data: collection, isLoading: collectionLoading} =
    useGetCollection(collectionId);

  // Get root URL without trailing slash
  let rootCatalogUrl = collection?.links?.find(
    link => link.rel === 'root',
  )?.href;

  if (rootCatalogUrl?.endsWith('/')) {
    rootCatalogUrl = rootCatalogUrl.slice(0, -1);
  }

  const baseLocation = locations?.find(
    loc => loc.catalogUrl === rootCatalogUrl,
  );

  return {
    url: baseLocation?.[type] || '',
    isLoading: locationsLoading || collectionLoading,
  };
};

export const useGetCatalogSearch = <
  T extends string | SearchResultFeature = SearchResultFeature,
>(
  query?: SearchQuery,
  options: {enabled?: boolean} = {enabled: true},
) => {
  const collectionId = query?.collections?.[0] || '';
  const {url, isLoading} = useGetServiceLocationForCollection(
    collectionId,
    SHURLs.catalog,
  );

  return useQuery({
    queryKey: catalogQueryKeys.list({query}),
    queryFn: () => {
      if (!url) {
        throw new Error('Hostname for catalog not found');
      }
      return getCatalogSearch<T>(query!, url);
    },
    staleTime: DURATION_MS.HOUR,
    enabled:
      !!query?.bbox &&
      !!query?.datetime &&
      !!query?.collections?.length &&
      options.enabled &&
      !isLoading,
  });
};
